/**
 * Canonical ICP NFT token identity helpers.
 * EXT holdings and indexes must share the same tokenId form or vaults duplicate.
 *
 * Convention (matches NFT Migrator OISY scan): tokenId = decimal token index string.
 * Encoded EXT TokenIdentifier principals are decoded back to the numeric index.
 */
import { Principal } from "@dfinity/principal";
const TID_PREFIX = [0x0a, 0x74, 0x69, 0x64]; // \x0Atid
/**
 * If `tokenId` is an EXT TokenIdentifier principal for `canisterId`, return its index.
 * Numeric strings return the parsed index. Opaque IDs return null.
 */
export function tryDecodeExtTokenIndex(canisterId, tokenId) {
    const trimmed = tokenId.trim();
    if (!trimmed)
        return null;
    if (/^\d+$/.test(trimmed))
        return Number(trimmed);
    try {
        const bytes = Principal.fromText(trimmed).toUint8Array();
        if (bytes.length < TID_PREFIX.length + 4)
            return null;
        for (let i = 0; i < TID_PREFIX.length; i++) {
            if (bytes[i] !== TID_PREFIX[i])
                return null;
        }
        const canisterBytes = Principal.fromText(canisterId).toUint8Array();
        const indexOffset = TID_PREFIX.length + canisterBytes.length;
        if (bytes.length !== indexOffset + 4)
            return null;
        for (let i = 0; i < canisterBytes.length; i++) {
            if (bytes[TID_PREFIX.length + i] !== canisterBytes[i])
                return null;
        }
        const view = new DataView(bytes.buffer, bytes.byteOffset + indexOffset, 4);
        return view.getUint32(0, false);
    }
    catch {
        return null;
    }
}
/**
 * Normalize ICP NFT tokenId to decimal index string when possible.
 * Keeps non-EXT opaque IDs unchanged.
 */
export function canonicalIcpTokenId(canisterId, tokenId) {
    const raw = typeof tokenId === "bigint" || typeof tokenId === "number"
        ? String(tokenId)
        : tokenId.trim();
    if (/^\d+$/.test(raw)) {
        const n = BigInt(raw);
        return n.toString();
    }
    const decoded = tryDecodeExtTokenIndex(canisterId, raw);
    if (decoded !== null)
        return String(decoded);
    return raw;
}
/** Dedupe key used across indexes, catalog scans, and vault merge. */
export function icpPortfolioNftKey(contractId, tokenId) {
    return `${contractId}:${canonicalIcpTokenId(contractId, tokenId)}`;
}
